import { useCallback, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import PrimitiveFileInput from '@/components/form/_primitives/PrimitiveFileInput.tsx';
import PrimitiveSingleSelect from '@/components/form/_primitives/PrimitiveSingleSelect.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import useImportBankTransactions from '@/features/dashboard/rawBankTransactions/hooks/useImportBankTransactions.ts';
import useGetSupportedBanks from '@/features/dashboard/rawBankTransactions/hooks/useGetSupportedBanks.ts';
import ImportResultBanner from '@/features/dashboard/rawBankTransactions/components/ImportResultBanner.tsx';
import { Bank } from '@/features/dashboard/rawBankTransactions/types/Bank.ts';
import { bankLabels } from '@/features/dashboard/rawBankTransactions/types/Bank.utils.ts';
import { SupportedBankDto } from '@/features/dashboard/rawBankTransactions/types/SupportedBankDto.ts';

const EXTENSION_MIME_MAP: Record<string, string> = {
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.sta': 'text/plain',
  '.mt940': 'text/plain',
  '.pdf': 'application/pdf',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function buildAccept(extensions: string[]): Accept {
  const accept: Accept = {};
  for (const ext of extensions) {
    const mime =
      EXTENSION_MIME_MAP[ext.toLowerCase()] ?? 'application/octet-stream';
    if (!accept[mime]) accept[mime] = [];
    if (!accept[mime].includes(ext)) accept[mime].push(ext);
  }
  return accept;
}

interface BankImportModalProps {
  onClose: () => void;
}

const BankImportModal = ({ onClose }: BankImportModalProps) => {
  const { data: supportedBanks, isLoading: isLoadingBanks } =
    useGetSupportedBanks();
  const mutation = useImportBankTransactions();
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  // Wynik importu nie niesie nazwy pliku, a podsumowanie ma powiedzieć, czego dotyczy.
  const [importedFileName, setImportedFileName] = useState<string>('');

  const bankOptions = useMemo(
    () =>
      (supportedBanks ?? []).map((bank: SupportedBankDto) => ({
        value: bank.code,
        label: bankLabels[bank.code] ?? bank.code,
      })),
    [supportedBanks],
  );

  const selectedBankDto = useMemo(
    () => supportedBanks?.find((b) => b.code === selectedBank),
    [supportedBanks, selectedBank],
  );

  const accept = useMemo(
    () =>
      selectedBankDto ? buildAccept(selectedBankDto.extensions) : undefined,
    [selectedBankDto],
  );

  const extensionsHint = selectedBankDto
    ? selectedBankDto.extensions.join(', ')
    : undefined;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0 && selectedBank) {
        setImportedFileName(acceptedFiles[0].name);
        mutation.mutate({ file: acceptedFiles[0], bank: selectedBank });
      }
    },
    [mutation, selectedBank],
  );

  const handleClose = () => {
    if (!mutation.isPending) {
      onClose();
    }
  };

  return (
    <Modal onClose={handleClose}>
      <ModalHeader title="Import transakcji bankowych" onClose={handleClose} />
      <ModalContent>
        {mutation.isSuccess && mutation.data ? (
          <ImportResultBanner
            result={mutation.data}
            fileName={importedFileName}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-neutral-600">
              Wybierz bank, z którego pobrałeś historię transakcji, a następnie
              wgraj plik w akceptowanym formacie. Pliki PDF nie są obsługiwane.
            </p>

            <div className="flex flex-col gap-1">
              <InputLabel label="Bank" required />
              {isLoadingBanks ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <PrimitiveSingleSelect
                  options={bankOptions}
                  value={selectedBank ?? undefined}
                  onChange={(value) => setSelectedBank((value as Bank) ?? null)}
                  placeholder="Wybierz bank..."
                  isDisabled={mutation.isPending}
                />
              )}
            </div>

            <PrimitiveFileInput
              onDrop={onDrop}
              accept={accept}
              disabled={!selectedBank || mutation.isPending}
              isLoading={mutation.isPending}
              loadingText="Importowanie..."
              hint={
                extensionsHint
                  ? `Akceptowane formaty: ${extensionsHint}`
                  : 'Najpierw wybierz bank'
              }
            />
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        {mutation.isSuccess ? (
          <ActionButton tone="accent" size="sm" onClick={handleClose}>
            Zamknij
          </ActionButton>
        ) : (
          <ActionButton
            tone="ghost"
            size="sm"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Anuluj
          </ActionButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default BankImportModal;
